<template>
        <div
                v-if="isOpen"
                class="mobile-sidebar-container"
        >
                <div
                        class="mobile-sidebar-backdrop"
                        @click="closeSidebar"
                />
                <aside class="mobile-sidebar-panel">
                                        <div class="py-4 text-gray-500">
                                                <div class="flex items-center justify-between px-6">
                                                        <router-link
                                                                to="/"
                                                                class="text-lg font-bold text-gray-800"
                                                        >
                                                                PRINTIKI
                                                        </router-link>
                                                        <button
                                                                class="p-1 rounded-md text-gray-500 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-5"
                                                                @click="closeSidebar"
                                                        >
                                                                <svg
                                                                        class="w-6 h-6"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                >
                                                                        <path
                                                                                stroke-linecap="round"
                                                                                stroke-linejoin="round"
                                                                                stroke-width="2"
                                                                                d="M6 18L18 6M6 6l12 12"
                                                                        />
                                                                </svg>
                                                        </button>
                                                </div>
                                                <ul class="mt-6">
                                                        <li
                                                                v-for="(node, index) in navigation"
                                                                :key="`nav-${index}`"
                                                                class="relative px-6 py-3"
                                                        >
                                                                <span
                                                                        v-if="isActiveRoute(node)"
                                                                        class="absolute inset-y-0 left-0 w-1 bg-orange-7 rounded-tr-lg rounded-br-lg"
                                                                        aria-hidden="true"
                                                                />
                                                                <template v-if="!node.sub">
                                                                        <router-link
                                                                                :to="node.to || '/'"
                                                                                class="inline-flex items-center w-full text-sm font-semibold transition-colors duration-150 hover:text-gray-800"
                                                                                :class="isActiveRoute(node) ? 'text-gray-800' : 'text-gray-500'"
                                                                                @click="closeSidebar"
                                                                        >
                                                                                <component
                                                                                        :is="getIconComponent(node.icon)"
                                                                                        class="w-5 h-5"
                                                                                />
                                                                                <span class="ml-4">{{ node.name }}</span>
                                                                        </router-link>
                                                                </template>
                                                                <template v-else>
                                                                        <button
                                                                                class="inline-flex items-center justify-between w-full text-sm font-semibold transition-colors duration-150 hover:text-gray-800"
                                                                                :class="isSubmenuOpen(node) ? 'text-gray-800' : 'text-gray-500'"
                                                                                @click="toggleSubmenu(index)"
                                                                        >
                                                                                <span class="inline-flex items-center">
                                                                                        <component
                                                                                                :is="getIconComponent(node.icon)"
                                                                                                class="w-5 h-5"
                                                                                        />
                                                                                        <span class="ml-4">{{ node.name }}</span>
                                                                                </span>
                                                                                <svg
                                                                                        class="w-4 h-4 transition-transform duration-200"
                                                                                        :class="{ 'rotate-180': openMenus[index] }"
                                                                                        fill="currentColor"
                                                                                        viewBox="0 0 20 20"
                                                                                >
                                                                                        <path
                                                                                                fill-rule="evenodd"
                                                                                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                                                clip-rule="evenodd"
                                                                                        />
                                                                                </svg>
                                                                        </button>
                                                                        <ul
                                                                                v-show="openMenus[index]"
                                                                                class="p-2 mt-2 space-y-2 overflow-hidden text-sm font-medium rounded-md bg-gray-100"
                                                                        >
                                                                                <li
                                                                                        v-for="(subNode, subIndex) in node.sub"
                                                                                        :key="`sub-${subIndex}`"
                                                                                        class="px-2 py-1 transition-colors duration-150 hover:text-gray-800"
                                                                                        :class="isActiveSubRoute(subNode) ? 'text-gray-800' : 'text-gray-500'"
                                                                                >
                                                                                        <router-link
                                                                                                :to="subNode.to || '/'"
                                                                                                class="w-full block"
                                                                                                @click="closeSidebar"
                                                                                        >
                                                                                                {{ subNode.name }}
                                                                                        </router-link>
                                                                                </li>
                                                                        </ul>
                                                                </template>
                                                        </li>
                                                </ul>
                                        </div>
                        </aside>
                </div>
</template>

<script setup>
import { reactive, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import menu from '../../../data/menu';
import { iconMap } from '../../icons';
import { useModuleAccess } from 'composables/useModuleAccess';

defineProps({
        isOpen: {
                type: Boolean,
                default: false,
        },
});

const emit = defineEmits(['close']);

const route = useRoute();
const { canAccessModule } = useModuleAccess();

const filteredNavigation = computed(() => {
        return menu.main
                .map((node) => {
                        if (!node.sub) {
                                return node;
                        }
                        const filteredSub = node.sub.filter(
                                (subNode) => !subNode.module || canAccessModule(subNode.module),
                        );
                        if (filteredSub.length === 0) {
                                return null;
                        }
                        return { ...node, sub: filteredSub };
                })
                .filter(Boolean);
});

const navigation = filteredNavigation;
const openMenus = reactive({});

watch(
        [() => route.path, filteredNavigation],
        () => {
                filteredNavigation.value.forEach((node, index) => {
                        if (node.sub && isSubmenuOpen(node)) {
                                openMenus[index] = true;
                        }
                });
        },
        { immediate: true },
);

function closeSidebar() {
        emit('close');
}

function toggleSubmenu(index) {
        openMenus[index] = !openMenus[index];
}

function isActiveRoute(node) {
        if (node.to) {
                return route.path === node.to || route.path.startsWith(node.to + '/');
        }
        if (node.sub) {
                return node.sub.some((sub) => route.path.startsWith(sub.to));
        }
        return false;
}

function isSubmenuOpen(node) {
        if (node.sub) {
                return node.sub.some((sub) => route.path.startsWith(sub.to));
        }
        return false;
}

function isActiveSubRoute(node) {
        return route.path.startsWith(node.to);
}

function getIconComponent(iconClass) {
        return iconMap[iconClass] || iconMap['si si-speedometer'];
}
</script>

<style scoped>
/* Mobile sidebar container - fixed overlay */
.mobile-sidebar-container {
        position: fixed !important;
        top: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        left: 0 !important;
        z-index: 9999 !important;
        display: block !important;
}

/* Backdrop - semi-transparent black overlay */
.mobile-sidebar-backdrop {
        position: fixed !important;
        top: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        left: 0 !important;
        background-color: rgba(0, 0, 0, 0.5) !important;
        z-index: 9999 !important;
}

/* Sidebar panel - white sidebar from left */
.mobile-sidebar-panel {
        position: fixed !important;
        top: 0 !important;
        bottom: 0 !important;
        left: 0 !important;
        width: 16rem !important;
        background-color: #ffffff !important;
        overflow-y: auto !important;
        z-index: 10000 !important;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

/* Hide on medium screens and up */
@media (min-width: 768px) {
        .mobile-sidebar-container {
                display: none !important;
        }
}

/* Content wrapper */
.mobile-sidebar-panel > div {
        padding-top: 1rem !important;
        padding-bottom: 1rem !important;
        color: #6b7280 !important;
}

/* List styles */
.mobile-sidebar-panel ul {
        list-style: none !important;
        padding-left: 0 !important;
        margin-bottom: 0 !important;
        margin-top: 0 !important;
}

.mobile-sidebar-panel li {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        position: relative !important;
        padding: 0.75rem 1.5rem !important;
}

.mobile-sidebar-panel li::before {
        display: none !important;
        content: none !important;
}

/* Link reset */
.mobile-sidebar-panel a {
        all: unset !important;
        cursor: pointer !important;
        display: inline-flex !important;
        align-items: center !important;
        width: 100% !important;
        font-size: 0.875rem !important;
        font-weight: 600 !important;
        color: #6b7280 !important;
        transition: color 0.15s ease-in-out !important;
}

.mobile-sidebar-panel a:link,
.mobile-sidebar-panel a:visited {
        color: inherit !important;
        text-decoration: none !important;
}

.mobile-sidebar-panel a:hover,
.mobile-sidebar-panel a:active {
        color: #1f2937 !important;
        text-decoration: none !important;
        outline: none !important;
        box-shadow: none !important;
        background: transparent !important;
}

.mobile-sidebar-panel a:focus-visible {
        outline: 2px solid #9333ea !important;
        outline-offset: 2px !important;
        border-radius: 0.25rem !important;
}

/* Button reset */
.mobile-sidebar-panel button {
        all: unset !important;
        cursor: pointer !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        width: 100% !important;
        font-size: 0.875rem !important;
        font-weight: 600 !important;
        color: #6b7280 !important;
        transition: color 0.15s ease-in-out !important;
}

.mobile-sidebar-panel button:hover {
        color: #1f2937 !important;
}

.mobile-sidebar-panel button:focus {
        outline: none !important;
        box-shadow: none !important;
}

.mobile-sidebar-panel button:focus-visible {
        outline: 2px solid #9333ea !important;
        outline-offset: 2px !important;
        border-radius: 0.25rem !important;
}

/* SVG icons */
.mobile-sidebar-panel svg {
        width: 1.25rem !important;
        height: 1.25rem !important;
        flex-shrink: 0 !important;
}

/* Submenu container */
.mobile-sidebar-panel ul ul {
        background-color: #f3f4f6 !important;
        border-radius: 0.375rem !important;
        padding: 0.5rem !important;
        padding-left: 2.5rem !important;
        margin-top: 0.5rem !important;
}

/* Submenu items */
.mobile-sidebar-panel ul ul li {
        padding: 0.375rem 0 !important;
}

.mobile-sidebar-panel ul ul a {
        font-size: 0.875rem !important;
        font-weight: 500 !important;
}

/* Logo/title styling */
.mobile-sidebar-panel .text-lg {
        font-size: 1.125rem !important;
        font-weight: 700 !important;
        color: #1f2937 !important;
}

/* Close button */
.mobile-sidebar-panel button.p-1 {
        padding: 0.25rem !important;
        border-radius: 0.375rem !important;
        color: #6b7280 !important;
}

.mobile-sidebar-panel button.p-1:hover {
        color: #1f2937 !important;
}
</style>
